import { Autocomplete, Checkbox, Chip } from "@mui/material";
import TextFieldMUI from "@mui/material/TextField";
import axios from "axios";
import { useStyletron } from "theme/styletron";
import { Box } from "components/Box/Box";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import formatDate from "utils/formatDate";

import * as S from "./style";

axios.defaults.headers.common["token"] = "3abd7f83-aae7-48fd-a4e5-77f19689921a";

function FilterPanel({
  onDataChange,
  onDisplayDataChange,
  isLoading,
  setIsLoading,
  canAccessHotelList,
}) {
  const isDgc = canAccessHotelList?.some(h => h.hotelId === "62fef2d3058c188c80ea0066") || false;
  
  const [css] = useStyletron();
  const [selectedDate, setSelectedDate] = useState("today");
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const defaultHotelList = canAccessHotelList;
  const [hotelList, setHotelList] = useState(defaultHotelList);
  
  // Set initial states, taking isDgc into account
  const [isCheckInSelected, setCheckInSelected] = useState(!isDgc);
  const [isIncompleteCheckInSelected, setIncompleteCheckInSelected] = useState(true);
  const [isCheckOutSelected, setCheckOutSelected] = useState(true);
  const [isCheckOutFailedSelected, setCheckOutFailedSelected] = useState(true);
  const [isWaitForRoomSelected, setWaitForRoomSelected] = useState(!isDgc);
  const [isKeyPickupSelected, setKeyPickupSelected] = useState(!isDgc);
  const [isKeyPickupFailedSelected, setKeyPickupFailedSelected] = useState(true);
  
  // Re-evaluate checkboxes if `isDgc` changes due to hotel access loading later
  useEffect(() => {
    if (isDgc) {
      setCheckInSelected(false);
      setWaitForRoomSelected(false);
      setKeyPickupSelected(false);
    }
  }, [isDgc]);

  const [responseType, setResponseType] = useState("0");
  const selectedGSR = "";

  useEffect(() => {
    const fetchData = async () => {
      if (isLoading) return;

      setIsLoading(true);

      const result = await axios
        .get(
          `${process.env.REACT_APP_API_URL}/dashboard?date_from=${formatDate(
            startDate
          )}&date_to=${formatDate(
            dayjs(endDate).add(1, "days")
          )}&by_date=${responseType}&specific_gsr=${selectedGSR}&hotelIds=${hotelList
            ?.map((item) => item.hotelId)
            ?.join(",")}`,

          {
            headers: { Authorization: `Bearer ${localStorage.ldapToken}` },
          }
        )
        .then((response) => response.data);

      setIsLoading(false);
      onDataChange(result, responseType, {
        checkin: isCheckInSelected,
        checkout: isCheckOutSelected,
        checkoutFailed: isCheckOutFailedSelected,
        keyPickup: isKeyPickupSelected,
        keyPickupFailed: isKeyPickupFailedSelected,
        waitForRoom: isWaitForRoomSelected,
        incompleteCheckin: isIncompleteCheckInSelected,
      });
    };

    fetchData();
    // Refetch only on date/hotel changes; checkbox filters update via onDisplayDataChange
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [responseType, startDate, endDate, hotelList]);

  useEffect(() => {
    setHotelList(canAccessHotelList || []);
  }, [canAccessHotelList]);

  useEffect(() => {
    onDisplayDataChange({
      checkin: isCheckInSelected,
      checkout: isCheckOutSelected,
      checkoutFailed: isCheckOutFailedSelected,
      waitForRoom: isWaitForRoomSelected,
      keyPickup: isKeyPickupSelected,
      keyPickupFailed: isKeyPickupFailedSelected,
      incompleteCheckin: isIncompleteCheckInSelected,
    });
  }, [
    isCheckInSelected,
    isCheckOutSelected,
    isCheckOutFailedSelected,
    isWaitForRoomSelected,
    isKeyPickupSelected,
    isKeyPickupFailedSelected,
    isIncompleteCheckInSelected,
    onDisplayDataChange,
  ]);

  const onDatePeriodSelect = (datePeriod) => {
    switch (datePeriod) {
      case "yesterday":
        setSelectedDate(datePeriod);
        setStartDate(dayjs().subtract(1, "days").toDate());
        setEndDate(dayjs().subtract(1, "days").toDate());
        break;
      case "last7days":
        setSelectedDate(datePeriod);
        setStartDate(() => dayjs().subtract(1, "weeks").toDate());
        setEndDate(new Date());
        break;
      case "last30days":
        setSelectedDate(datePeriod);
        setStartDate(() => dayjs().subtract(1, "months").toDate());
        setEndDate(new Date());
        break;
      case "today":
      default:
        setSelectedDate(datePeriod);
        setStartDate(new Date());
        setEndDate(new Date());
    }
  };

  return (
    <Box>
      <S.DatePeriodSelect>
        <div
          className={css({
            display: "flex",
          })}
        >
          <S.DatePeriod
            selected={selectedDate === "today"}
            onClick={() => {
              onDatePeriodSelect("today");
              setResponseType(() => "0");
            }}
          >
            Today
          </S.DatePeriod>
          <S.DatePeriod
            selected={selectedDate === "yesterday"}
            onClick={() => {
              onDatePeriodSelect("yesterday");
              setResponseType(() => "0");
            }}
          >
            Yesterday
          </S.DatePeriod>
          <S.DatePeriod
            selected={selectedDate === "last7days"}
            onClick={() => {
              onDatePeriodSelect("last7days");
              setResponseType(() => "1");
              setStartDate(() =>
                dayjs().subtract(1, "weeks").add(1, "day").toDate()
              );
              setEndDate(() => dayjs().toDate());
            }}
          >
            Last 7 Days
          </S.DatePeriod>
          <S.DatePeriod
            selected={selectedDate === "last30days"}
            onClick={() => {
              onDatePeriodSelect("last30days");
              setResponseType(() => "1");
              setStartDate(() => dayjs().subtract(1, "months").toDate());
              setEndDate(() => dayjs().toDate());
            }}
          >
            Last 30 Days
          </S.DatePeriod>
          <S.DatePeriod selected={selectedDate === "customPeriod"}>
            Custom period
          </S.DatePeriod>
        </div>
        <S.DateRangeSelectContainer>
          <div className="text">From</div>
          <div>
            <DatePicker
              className="date-picker"
              selected={startDate}
              value={dayjs(startDate).format("DD/MM/YYYY")}
              onChange={(date) => {
                setResponseType(() => "1");
                setStartDate(() => date);
                setSelectedDate(() => "customPeriod");
              }}
            />
          </div>
          <div className="text">To</div>
          <div>
            <DatePicker
              className="date-picker"
              selected={endDate}
              value={dayjs(endDate).format("DD/MM/YYYY")}
              onChange={(date) => {
                setResponseType(() => "1");
                setEndDate(() => date);
                setSelectedDate(() => "customPeriod");
              }}
            />
          </div>
        </S.DateRangeSelectContainer>
      </S.DatePeriodSelect>
      <S.HotelSelectContainer>
        {canAccessHotelList && canAccessHotelList.length > 1 && (
          <Autocomplete
            multiple
            options={defaultHotelList || []}
            disableClearable
            disableCloseOnSelect
            value={hotelList}
            isOptionEqualToValue={(option, value) =>
              option.hotelId === value.hotelId
            }
            onChange={(event, newValue) => {
              if (newValue.length === 0) return;
              setHotelList(newValue);
            }}
            getOptionLabel={(option) => option.hotelName}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const tagProps = getTagProps({ index });

                return (
                  <Chip
                    {...tagProps}
                    key={option.hotelId}
                    label={option.hotelName}
                    onDelete={value.length > 1 ? tagProps.onDelete : undefined}
                  />
                );
              })
            }
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox checked={selected} />
                {option.hotelName}
              </li>
            )}
            renderInput={(params) => (
              <TextFieldMUI
                {...params}
                label="Hotel"
                variant="standard"
                fullWidth
              />
            )}
          />
        )}
      </S.HotelSelectContainer>
      <div
        className={css({
          display: "flex",
          width: "100%",
        })}
      >
        <div
          className={css({
            display: "flex",
            flexDirection: "column",
            width: "100%",
          })}
        >
          <S.DataTypeContainer>
            <div onClick={() => setCheckInSelected(!isCheckInSelected)}>
              <input type="checkbox" checked={isCheckInSelected} />
              <label>Check-In</label>
            </div>

            <div onClick={() => setCheckOutSelected(!isCheckOutSelected)}>
              <input type="checkbox" checked={isCheckOutSelected} />
              <label>Check-Out</label>
            </div>
            <div
              onClick={() =>
                setCheckOutFailedSelected(!isCheckOutFailedSelected)
              }
            >
              <input type="checkbox" checked={isCheckOutFailedSelected} />
              <label>Check-Out Failed</label>
            </div>

            <div onClick={() => setKeyPickupSelected(!isKeyPickupSelected)}>
              <input type="checkbox" checked={isKeyPickupSelected} />
              <label>Key Pickup</label>
            </div>

            <div
              onClick={() =>
                setKeyPickupFailedSelected(!isKeyPickupFailedSelected)
              }
            >
              <input type="checkbox" checked={isKeyPickupFailedSelected} />
              <label>Unsuccessful Key Pickup</label>
            </div>

            <div onClick={() => setWaitForRoomSelected(!isWaitForRoomSelected)}>
              <input type="checkbox" checked={isWaitForRoomSelected} />
              <label>Wait For Room</label>
            </div>
            <div
              onClick={() =>
                setIncompleteCheckInSelected(!isIncompleteCheckInSelected)
              }
              style={{ width: "auto" }}
            >
              <input type="checkbox" checked={isIncompleteCheckInSelected} />
              <label>Incomplete Check-In</label>
            </div>
          </S.DataTypeContainer>
        </div>
      </div>
    </Box>
  );
}

export default FilterPanel;
